import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Avatar, Box, useTheme } from '@mui/material';
import type { NavigateFunction } from 'react-router-dom-v5-compat';
import { FormRowActionMenu } from 'src/components/organisms/FormRowActionMenu';
import { getSlugFromFormType } from 'src/components/pages/FormCreation/helpers/formTypeMapper';
import { getInitials } from 'src/utils/getInitials';
import { DateFormats, formatDate } from 'src/utils/time';
import { FormTitleCell } from './formManagementTable.styles';
import { renderFormManagementStatusBadge } from './formManagementStatusBadge';
import {
  type FormManagementColumnKey,
  getFormManagementScalarCell,
} from './formManagementTableConfig';
import { getFormManagementActionMenuConfig } from './getHiddenActionsForStatus';
import { FormType, type FormData } from './types';

const CreatedByCell = ({ name }: { name: string }) => {
  const { presets } = useTheme<LensTheme>();
  const { getAvatarProps } = presets.AvatarPresets;
  if (!name) return <>—</>;
  const initials = getInitials(name);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Avatar
        {...getAvatarProps({ size: 'medium', uniqueId: name })}
        aria-label={name}
      >
        {initials}
      </Avatar>
      {name}
    </Box>
  );
};

export function formatFormManagementYmd(iso: string) {
  return formatDate(iso, DateFormats.DAY_MONTH_YEAR_LONG, false, '');
}

export function renderFormManagementLanguageCount(form: FormData) {
  const langs = (form.languages ?? '')
    .split(',')
    .map((l) => l.trim())
    .filter(Boolean);

  return langs.length;
}

export function buildFormManagementTableData(args: {
  paginatedForms: FormData[];
  columnKeys: FormManagementColumnKey[];
  navigate: NavigateFunction;
  formatYmd: (iso: string) => string;
  onDuplicate: (form: FormData) => void;
  onArchive: (form: FormData) => void;
  onDelete: (form: FormData) => void;
  onAuditLog: (form: FormData) => void;
  onUnpublish?: (form: FormData) => void;
}) {
  const {
    paginatedForms,
    columnKeys,
    navigate,
    formatYmd,
    onDuplicate,
    onArchive,
    onDelete,
    onAuditLog,
    onUnpublish,
  } = args;

  return paginatedForms.map((form) => ({
    row: form.id,
    cellData: columnKeys.map((key) => {
      if (key === 'actions') {
        return (
          <FormRowActionMenu
            key={form.id}
            formId={form.id}
            onEdit={() => {
              const slug = getSlugFromFormType(form.type);
              navigate(`/form-creation/${slug}/${form.id}`);
            }}
            onDuplicate={() => onDuplicate(form)}
            onArchive={() => onArchive(form)}
            onDelete={() => onDelete(form)}
            onAuditLog={() => onAuditLog(form)}
            onUnpublish={
              onUnpublish && form.type === FormType.CONFLICT_OF_INTEREST
                ? () => onUnpublish(form)
                : undefined
            }
            {...getFormManagementActionMenuConfig(form.status)}
            data-testid={`forms-table-row-action-menu-${form.id}`}
          />
        );
      }
      if (key === 'title') {
        return <FormTitleCell title={form.title}>{form.title}</FormTitleCell>;
      }
      if (key === 'languages') return renderFormManagementLanguageCount(form);
      if (key === 'state') {
        return renderFormManagementStatusBadge(form.status);
      }
      if (key === 'createdBy') {
        return <CreatedByCell name={form.createdBy} />;
      }
      return getFormManagementScalarCell(form, key, formatYmd);
    }),
  }));
}
